import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, checkIn, checkOut, fetchTodayAttendance } from '../api/client';
import type { AttendanceActionResult } from '../api/types';
import { ConfirmDialog } from '../app/ConfirmDialog';
import { useSession } from '../app/WorkforceContext';
import { describeGeoStatus, useGeolocation } from '../geo/useGeolocation';
import {
  canSubmit,
  evaluateCheckIn,
  geoBoxState,
  outsideRadiusReasonApplies,
  selfieSatisfied,
  type CheckInReadiness,
  type WorkMode,
} from '../lib/attendance-readiness';
import { checkModeOnError, resolveCheckMode } from '../lib/check-mode';
import { periksaPulangCepat } from '../lib/pulang-terlalu-cepat';
import { presentError } from '../lib/error-messages';
import {
  formatAccuracy,
  formatDistance,
  formatLateMinutes,
  formatVerification,
  isUnverified,
} from '../lib/format';
import { nearestWorkLocation } from '../lib/geo-distance';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import { LoadingState } from '../states';
import { SELFIE_VALUE_EMPTY, SelfieCapture, type SelfieValue } from './SelfieCapture';

const WORK_MODES: readonly { readonly value: WorkMode; readonly label: string }[] = [
  { value: 'WFO', label: 'Di kantor' },
  { value: 'WFH', label: 'Dari rumah' },
  { value: 'FIELD_WORK', label: 'Kerja lapangan' },
  { value: 'BUSINESS_TRIP', label: 'Perjalanan dinas' },
];

/**
 * Layar Check In / Check Out.
 *
 * Prinsip layar ini: **beri tahu sebelum, bukan setelah.** Akurasi GPS tampil sebagai angka,
 * kolom alasan muncul saat memang dibutuhkan, dan tombol kirim dimatikan hanya ketika server
 * dipastikan menolak — bukan setiap kali ada yang kurang sempurna.
 *
 * Keputusan akhir tetap milik server. Yang ada di sini hanya perkiraan agar pengguna tidak
 * menempuh perjalanan bolak-balik demi mengetahui alasan penolakan.
 */
export function CheckPage(): ReactNode {
  const session = useSession();
  const geo = useGeolocation();

  // Satu pemegang kunci per pemasangan layar: percobaan ulang memakai kunci yang sama,
  // dan kunci baru hanya lahir setelah satu niat benar-benar selesai.
  const keyHolder = useRef(createIdempotencyKeyHolder());

  const [mode, setMode] = useState<'CHECK_IN' | 'CHECK_OUT' | 'loading' | 'unavailable'>('loading');
  const [workMode, setWorkMode] = useState<WorkMode>('WFO');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [locationNote, setLocationNote] = useState('');
  const [selfie, setSelfie] = useState<SelfieValue>(SELFIE_VALUE_EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AttendanceActionResult | null>(null);
  /*
   * Jam masuk menurut SERVER, bukan yang diingat layar ini. Keadaan yang dijaga sering
   * melibatkan halaman yang dimuat ulang — dan ingatan layar hilang tepat pada saat ia
   * paling dibutuhkan.
   */
  const [masukHariIni, setMasukHariIni] = useState<string | null>(null);
  const [konfirmasiPulang, setKonfirmasiPulang] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  /**
   * Tombol ditentukan SERVER, bukan disimpulkan di sini.
   *
   * `GET /api/me/attendance/today` menjawab `nextAction` memakai kebijakan dan zona waktu
   * organisasi — termasuk sesi yang dimulai kemarin dan belum ditutup. Klien tidak lagi
   * menghitung hari kerja dari jam perangkat.
   *
   * Kegagalan TIDAK LAGI jatuh diam-diam ke CHECK_IN. Menawarkan Check In kepada orang yang
   * sesinya masih terbuka membuat penolakan baru terlihat setelah tombol ditekan, dan pada
   * pilot 16 Agustus 2026 itu membuat absen pulang mustahil dilakukan lewat layar. Sekarang
   * kegagalan tampil sebagai kegagalan.
   */
  useEffect(() => {
    let cancelled = false;
    fetchTodayAttendance(session.auth)
      .then((status) => {
        if (cancelled) return;
        setMode(resolveCheckMode(status.nextAction));
        setMasukHariIni(status.today?.firstCheckInAt ?? null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setMode(checkModeOnError());
        if (cause instanceof ApiError) setError(cause);
      });
    return () => {
      cancelled = true;
    };
  }, [session.auth]);

  const nearest = useMemo(() => {
    if (geo.location.latitude === null || geo.location.longitude === null) return null;
    return nearestWorkLocation(
      { latitude: geo.location.latitude, longitude: geo.location.longitude },
      session.workLocations,
    );
  }, [geo.location, session.workLocations]);

  const readiness: CheckInReadiness | null = useMemo(() => {
    if (mode !== 'CHECK_IN' || !session.policy) return null;
    return evaluateCheckIn(
      {
        workMode,
        location: geo.location,
        outsideRadiusReason: reason,
        selfieFileId: selfie.selfieFileId,
        noSelfieReason: selfie.noSelfieReason,
      },
      session.policy,
      nearest,
    );
  }, [mode, session.policy, workMode, geo.location, reason, selfie, nearest]);

  /*
   * SENGAJA tidak bergantung pada `reason` — dan daftar dependensinya membuktikannya. Begitu
   * `reason` masuk ke sini, kolomnya akan menghilang lagi saat diketik.
   *
   * Override server (PR #1330, 24 Sep 2026, kurir Damha terjebak di lapangan) hidup di dalam
   * `outsideRadiusReasonApplies` sendiri (lihat dokumentasi & test di sana) — di sini hanya
   * menyalurkan `focusField` dari galat terakhir, bukan mengevaluasinya inline.
   */
  const perluAlasan = useMemo(() => {
    if (mode !== 'CHECK_IN' || !session.policy) return false;
    return outsideRadiusReasonApplies(
      { workMode, location: geo.location },
      session.policy,
      nearest,
      error ? presentError(error.code, error.status).focusField : null,
    );
  }, [mode, session.policy, workMode, geo.location, nearest, error]);

  /*
   * Keadaan kartu lokasi (item 8, review 12 Sep 2026) — lihat dokumentasi `geoBoxState` untuk
   * bukti bahwa ini tidak pernah bertentangan dengan `readiness` di atas. Absen pulang TIDAK
   * PERNAH dievaluasi geofence (ADR-0034 §5, sama seperti `blocked` di bawah), jadi di luar
   * `CHECK_IN` kartu ini selalu netral.
   */
  const geoState = useMemo(() => {
    if (mode !== 'CHECK_IN' || !session.policy) return { tone: 'neutral' as const, message: null };
    return geoBoxState({ workMode, location: geo.location }, session.policy, nearest);
  }, [mode, session.policy, workMode, geo.location, nearest]);

  /*
   * Pesan di luar radius kini tercetak DI DALAM kartu lokasi (`geoState.message`), bukan lagi di
   * kotak terpisah `ReadinessNotice` — kotak kuning di bawah DIHAPUS untuk kasus ini (item 8).
   * Ditahan HANYA saat `geoState` memang sedang berkata sesuatu (`tone !== 'neutral'`) DAN
   * `readiness` bukan kasus lain yang kebetulan juga aktif (swafoto, akurasi) — kasus lain itu
   * tetap harus tampil, dan pembuktian bahwa keduanya tidak pernah tumpang tindih ada di
   * `geoBoxState`.
   */
  const tahanCatatanKesiapan =
    geoState.tone !== 'neutral' &&
    readiness !== null &&
    (readiness.kind === 'warn' ||
      readiness.kind === 'block' ||
      (readiness.kind === 'need' && readiness.field === 'outsideRadiusReason'));

  if (mode === 'loading') {
    return <LoadingState label="Memeriksa status sesi Anda…" />;
  }

  /**
   * Status tidak diketahui — dan itu dinyatakan, bukan ditebak.
   *
   * Versi sebelumnya jatuh ke Check In saat gagal. Bagi orang yang sesinya masih terbuka, itu
   * menawarkan tombol yang PASTI ditolak server, dan menyembunyikan tombol yang sebenarnya ia
   * butuhkan. Menampilkan ketidaktahuan lebih berguna daripada menampilkan tebakan yang keliru.
   */
  if (mode === 'unavailable') {
    return (
      <section className="card">
        <h2>Status sesi belum dapat dibaca</h2>
        <p className="muted">
          {error
            ? presentError(error.code, error.status).hint
            : 'Layanan absensi sedang tidak dapat dihubungi. Muat ulang halaman untuk mencoba lagi.'}
        </p>
      </section>
    );
  }

  if (result) {
    return <ResultCard result={result} onAgain={() => setResult(null)} />;
  }

  const geoInfo = describeGeoStatus(geo.status);
  /*
   * K3, review 12 Sep 2026 — selfie berlaku SAMA untuk absen pulang, TIDAK seperti gerbang
   * lokasi (ADR-0034 §5) yang sengaja hanya berlaku untuk check-in. `selfieSatisfied` mode-
   * agnostik persis karena ini — lihat dokumentasinya di `attendance-readiness.ts`.
   */
  const checkOutSelfieBlocked =
    mode === 'CHECK_OUT' &&
    session.policy !== null &&
    !selfieSatisfied(session.policy, selfie.selfieFileId, selfie.noSelfieReason);
  // Absen pulang TIDAK PERNAH ditolak karena lokasi (ADR-0034 §5) — tombolnya tidak boleh
  // dimatikan oleh evaluasi lokasi apa pun, hanya oleh selfie di atas.
  const blocked =
    (mode === 'CHECK_IN' && readiness !== null && !canSubmit(readiness)) || checkOutSelfieBlocked;
  const noPolicy = session.policy === null;

  /**
   * Pintu masuk tombol — bertanya lebih dulu bila absen pulang datang terlalu cepat.
   *
   * Pemeriksaannya di SINI, bukan di dalam `submit`, supaya `submit` tetap berarti "kirim
   * sekarang" dan dapat dipanggil ulang oleh dialog tanpa memicu dirinya sendiri kembali.
   */
  function minta(): void {
    if (mode === 'CHECK_OUT') {
      const putusan = periksaPulangCepat(masukHariIni, new Date());
      if (putusan.perluKonfirmasi) {
        setKonfirmasiPulang(putusan.pesan);
        return;
      }
    }
    void submit();
  }

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    const key = keyHolder.current.current();
    const base = {
      occurredAtDevice: new Date().toISOString(),
      latitude: geo.location.latitude,
      longitude: geo.location.longitude,
      accuracyMeters: geo.location.accuracyMeters,
      locationNote: locationNote.trim() === '' ? null : locationNote.trim(),
      note: note.trim() === '' ? null : note.trim(),
      selfieFileId: selfie.selfieFileId,
      noSelfieReason: selfie.noSelfieReason.trim() === '' ? null : selfie.noSelfieReason.trim(),
    };
    try {
      const response =
        mode === 'CHECK_IN'
          ? await checkIn(session.auth, key, {
              ...base,
              workMode,
              workLocationId: null,
              outsideRadiusReason: reason.trim() === '' ? null : reason.trim(),
            })
          : await checkOut(session.auth, key, base);
      // Niat selesai — kunci berikutnya harus baru, jika tidak absen pulang berikutnya akan
      // dianggap pengulangan dan tidak mencatat apa pun.
      keyHolder.current.rotate();
      setResult(response);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const presented = error ? presentError(error.code, error.status) : null;

  return (
    <>
      {/*
        Konfirmasi absen pulang yang datang terlalu cepat sesudah absen masuk.

        BUKAN penolakan: ada pekerjaan yang memang sebentar, dan server yang menolak akan membuat
        orang seperti itu membiarkan sesinya menggantung sampai tengah malam — lebih buruk daripada
        nol menit, karena ia merusak hari berikutnya juga. Yang dicegah bukan absen pulang yang
        singkat, melainkan absen pulang yang TIDAK DIMAKSUDKAN (kejadian 31 Agu 2026: masuk dan
        pulang berjarak 28 detik, hari kerjanya tercatat nol menit).
      */}
      <ConfirmDialog
        open={konfirmasiPulang !== null}
        title="Absen pulang sekarang?"
        message={konfirmasiPulang ?? ''}
        confirmLabel="Ya, sudah selesai"
        cancelLabel="Belum, batalkan"
        onConfirm={() => {
          setKonfirmasiPulang(null);
          void submit();
        }}
        onDismiss={() => setKonfirmasiPulang(null)}
      />

      <section className="card">
        <h2>{mode === 'CHECK_IN' ? 'Absen masuk' : 'Absen pulang'}</h2>

        <div
          className={
            geoState.tone === 'warn' || geo.status !== 'ready' ? 'geo-box warn' : 'geo-box'
          }
        >
          <p className="geo-title">{geoInfo.title}</p>
          {/* Akurasi dalam ANGKA. Centang hijau menenangkan; angka dapat ditindaklanjuti. */}
          <p className="geo-accuracy">{formatAccuracy(geo.location.accuracyMeters)}</p>
          {nearest ? (
            <p className="muted small">
              {nearest.name} · {formatDistance(nearest.distanceMeters)} dari titik Anda (radius{' '}
              {nearest.geofenceRadiusMeters} m)
            </p>
          ) : null}
          {geoInfo.hint ? <p className="muted small">{geoInfo.hint}</p> : null}
          {/*
            Di luar radius (item 8, review 12 Sep 2026) — DI DALAM kartu ini, bukan kotak
            terpisah di bawah. `warn-text` yang sama dipakai layar lain untuk pesan yang harus
            terbaca tanpa mengandalkan warna saja.
          */}
          {geoState.message ? <p className="warn-text">{geoState.message}</p> : null}
          {geo.status !== 'ready' && geo.status !== 'locating' ? (
            <button type="button" onClick={geo.retry}>
              Coba lagi baca lokasi
            </button>
          ) : null}
        </div>
      </section>

      {noPolicy ? (
        <section className="card error" role="alert">
          <h3>Kebijakan absensi belum aktif</h3>
          <p>
            Organisasi Anda belum mengaktifkan kebijakan absensi, sehingga server akan menolak absen
            masuk. Hubungi admin organisasi Anda.
          </p>
        </section>
      ) : null}

      <section className="card">
        {mode === 'CHECK_IN' ? (
          <>
            <label htmlFor="workMode">Metode kerja</label>
            <select
              id="workMode"
              value={workMode}
              onChange={(e) => setWorkMode(e.target.value as WorkMode)}
            >
              {WORK_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </>
        ) : null}

        <label htmlFor="locationNote">Catatan lokasi (opsional)</label>
        <input
          id="locationNote"
          value={locationNote}
          onChange={(e) => setLocationNote(e.target.value)}
          maxLength={200}
          placeholder="mis. Gudang B"
        />

        <label htmlFor="note">Catatan (opsional)</label>
        <input id="note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />

        {/*
          Kemunculan kolom ini diambil dari KEBERLAKUAN, bukan dari hasil validasi.

          Sebelumnya syaratnya `readiness.kind === 'need'` — dan `readiness` berhenti berkata
          `need` begitu alasannya terisi, sehingga kolomnya lenyap setelah SATU karakter diketik.
          Fokus ikut hilang dan sisa kalimat mengetik ke ruang kosong, jadi yang tersimpan "M",
          bukan "Mengantar barang ke klien". Ditemukan 23 Agu saat memakai aplikasi sebagai kurir.
        */}
        {perluAlasan ? (
          <>
            <label htmlFor="reason">Alasan berada di luar radius</label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="mis. Mengantar barang ke klien"
            />
            {/*
              Jalan pintas kedua (24 Sep 2026) — kadang penyebabnya bukan "memang di luar radius"
              melainkan GPS yang sempat membaca salah (umum di dalam gedung beratap logam).
              `geo.retry()` MEMBUANG pembacaan lama dan meminta ulang dari nol (lihat
              `useGeolocation` — `watchPosition` baru, bukan sekadar membaca cache) — kalau
              pembacaan berikutnya ternyata masuk radius, kolom ini akan hilang sendiri lain kali
              `perluAlasan` dihitung ulang, dan orangnya tidak perlu menulis alasan sama sekali.
              Ditawarkan BERSAMA kolom alasan, bukan menggantikannya: mengetik tetap jalan tercepat.
            */}
            <button type="button" onClick={geo.retry}>
              Coba lagi baca lokasi
            </button>
          </>
        ) : null}

        {/*
          K3, review 12 Sep 2026 — berlaku SAMA untuk check-in dan check-out, karena itu dirender
          di luar cabang `mode === 'CHECK_IN'` di atas. Disembunyikan sepenuhnya saat DISABLED —
          organisasi yang tidak memakai selfie tidak perlu melihat kartunya sama sekali.
        */}
        {session.policy && session.policy.selfiePolicy !== 'DISABLED' ? (
          <SelfieCapture
            auth={session.auth}
            required={session.policy.selfiePolicy === 'REQUIRED'}
            value={selfie}
            onChange={setSelfie}
            disabled={submitting}
          />
        ) : null}
      </section>

      {readiness && !tahanCatatanKesiapan ? <ReadinessNotice readiness={readiness} /> : null}

      {presented ? (
        <section className="card error" role="alert">
          <h3>{presented.title}</h3>
          <p>{error?.message}</p>
          <p className="muted small">{presented.hint}</p>
          {error?.correlationId ? (
            <p className="muted small">Kode rujukan: {error.correlationId}</p>
          ) : null}
          {/* Tombol ulang hanya untuk kegagalan yang memang bisa berhasil bila diulang. */}
          {presented.canRetry ? (
            <button type="button" onClick={minta} disabled={submitting}>
              Coba lagi
            </button>
          ) : null}
        </section>
      ) : null}

      <button
        type="button"
        className="button-primary big"
        disabled={submitting || blocked || noPolicy}
        onClick={minta}
      >
        {submitting
          ? 'Mengirim…'
          : mode === 'CHECK_IN'
            ? 'Kirim absen masuk'
            : 'Kirim absen pulang'}
      </button>
    </>
  );
}

function ReadinessNotice({ readiness }: { readonly readiness: CheckInReadiness }): ReactNode {
  if (readiness.kind === 'ready') return null;
  const role = readiness.kind === 'block' ? 'alert' : 'status';
  const className = readiness.kind === 'block' ? 'card error' : 'card warn';
  const title =
    readiness.kind === 'block'
      ? 'Belum dapat absen masuk'
      : readiness.kind === 'need'
        ? 'Ada yang perlu dilengkapi'
        : 'Perhatikan sebelum melanjutkan';
  return (
    <section className={className} role={role}>
      <h3>{title}</h3>
      <p>{readiness.message}</p>
    </section>
  );
}

function ResultCard({
  result,
  onAgain,
}: {
  readonly result: AttendanceActionResult;
  readonly onAgain: () => void;
}): ReactNode {
  const unverified = isUnverified(result.locationVerification);
  return (
    <section className={unverified ? 'card warn' : 'card ok'} role="status">
      <h2>Tercatat</h2>
      <dl className="pairs">
        <dt>Hari kerja</dt>
        <dd>{result.workDate}</dd>
        {/*
          Ditemukan 24 Agu 2026: layar konfirmasi ini TIDAK PERNAH menyebut keterlambatan,
          padahal `result.day.lateMinutes` sudah dikirim server sejak awal — hanya belum
          ditampilkan. Kurir yang absen masuk terlambat menutup layar ini tanpa tahu ia
          terlambat, apalagi berapa menit, sampai membuka Riwayat terpisah.
        */}
        {result.day && result.day.lateMinutes > 0 ? (
          <>
            <dt>Keterlambatan</dt>
            <dd>{formatLateMinutes(result.day.lateMinutes)}</dd>
          </>
        ) : null}
        <dt>Verifikasi lokasi</dt>
        {/* Nilai SERVER, ditampilkan apa adanya — klien tidak pernah menghitung ulang. */}
        <dd>{formatVerification(result.locationVerification)}</dd>
        {result.distanceMeters !== null ? (
          <>
            <dt>Jarak</dt>
            <dd>{formatDistance(result.distanceMeters)}</dd>
          </>
        ) : null}
      </dl>
      {unverified ? (
        <p className="warn-text">
          Absensi ini tercatat tanpa verifikasi lokasi penuh dan dapat ditanyakan manajer Anda.
        </p>
      ) : null}
      <button type="button" onClick={onAgain}>
        Selesai
      </button>
    </section>
  );
}
